import { useEffect, useRef, useCallback } from 'preact/hooks';
import { decodeEntities } from '@wordpress/html-entities';
import CloseIcon from '../../icons/CloseIcon';
import ExternalLinkIcon from '../../icons/ExternalLinkIcon';
import { useLocationDetail } from '../../hooks/useLocationDetail';

type LocationDialogProps = {
	location: MapLocation | null;
	open: boolean;
	onClose: () => void;
	mapType: string;
};

/**
 * Truncate text to a maximum length, adding an ellipsis if truncated.
 *
 * @param {string} text      - The text to truncate.
 * @param {number} maxLength - Maximum character length.
 * @return {string} Truncated text.
 */
function truncate( text: string, maxLength: number ): string {
	if ( text.length <= maxLength ) {
		return text;
	}
	return text.slice( 0, maxLength ).trimEnd() + '\u2026';
}

/**
 * Location detail dialog component.
 * Uses native HTML <dialog> with showModal() for proper modal behavior.
 *
 * @param {Object}           props          - Component props.
 * @param {MapLocation|null} props.location - Location data to display.
 * @param {boolean}          props.open     - Whether the dialog is open.
 * @param {Function}         props.onClose  - Callback when the dialog closes.
 * @return {*} jsx
 */
export default function LocationDialog( { location, open, onClose, mapType }: LocationDialogProps ) {
	const dialogRef = useRef< HTMLDialogElement >( null );
	const { detail, isLoading: detailLoading, error: detailError } = useLocationDetail( open ? location?.id ?? null : null );
	const { title, city, state, color, location_type, url, linking_mode } = location ?? {};
	const { description } = detail ?? {};

	// Remember which element had focus before the dialog opened, so we can restore it on close.
	const triggerRef = useRef< HTMLElement | null >( null );

	// Handle dialog open / close via native dialog methods.
	const handleToggle = useCallback( ( openState: boolean | null ) => {
		if ( ! dialogRef.current ) {
			return;
		}

		const dialogIsOpen = dialogRef.current.open;
		const newIsOpen = openState ?? ! dialogIsOpen;

		if ( newIsOpen === dialogIsOpen ) {
			return;
		}

		if ( newIsOpen ) {
			triggerRef.current = document.activeElement as HTMLElement;
			dialogRef.current.showModal();
			document.body.style.overflow = 'hidden';

			// Focus the inner content area so screen readers start at the top.
			dialogRef.current.querySelector< HTMLElement >( '.str-maps__dialog-inner' )?.focus();
		} else {
			dialogRef.current.close();
		}
	}, [] );

	// Open / close dialog when open prop changes.
	useEffect( () => {
		handleToggle( open );

		// Ensure scroll is restored if the component unmounts while open.
		return () => {
			document.body.style.overflow = '';
		};
	}, [ open, handleToggle ] );

	// Light dismiss on backdrop click + native close event.
	useEffect( () => {
		const dialogEl = dialogRef.current;
		if ( ! dialogEl ) {
			return;
		}

		const handleClick = ( event: MouseEvent ) => {
			// Clicking the <dialog> element itself (the backdrop) closes the dialog.
			if ( event.target === dialogEl ) {
				handleToggle( false );
			}
		};

		const handleClose = () => {
			document.body.style.overflow = '';
			onClose();

			// Return focus to the element that opened the dialog.
			triggerRef.current?.focus();
			triggerRef.current = null;
		};

		dialogEl.addEventListener( 'click', handleClick );
		dialogEl.addEventListener( 'close', handleClose );

		return () => {
			dialogEl.removeEventListener( 'click', handleClick );
			dialogEl.removeEventListener( 'close', handleClose );
		};
	}, [ handleToggle, onClose ] );

	const isExternal = linking_mode === 'ext';

	return (
		<dialog ref={ dialogRef } className={ `str-maps__dialog ${ open ? 'str-maps__dialog--open' : '' }` } aria-labelledby={ location ? 'str-maps-dialog-title' : undefined }>
			{ location && (
				<>
					<button
						className="str-maps__dialog-close"
						aria-label="Close dialog"
						onClick={ () => handleToggle( false ) }
					>
						<CloseIcon />
					</button>
					<div className={ `str-maps__dialog-inner ${ mapType === 'pif-map' ? 'str-maps__dialog-inner--pif' : '' }` } tabIndex={ -1 }>
						<h2 id="str-maps-dialog-title" className="str-maps__dialog-title h4">
							{ title ? decodeEntities( title ) : '' }
						</h2>
						<p className="str-maps__dialog-location">
							{ city?.length ? `${city},` : '' } { state }
						</p>
						{ detailLoading && (
							<p className="str-maps__dialog-excerpt str-maps__dialog-excerpt--loading">
								Loading…
							</p>
						) }
						{ description && ! detailLoading && (
							<p className="str-maps__dialog-excerpt">
								{ truncate( decodeEntities( description ), 600 ) }
							</p>
						) }
						{ detailError && ! detailLoading && (
							<p className="str-maps__dialog-excerpt str-maps__dialog-excerpt--error">
								Unable to load details.
							</p>
						) }
						{ !! location_type && (
							<span
								className={ `str-maps__dialog-tag ${ color ? 'has-custom-color' : '' }` }
								style={ color ? { '--str-maps-location-color': color } as Record< string, string > : undefined }
							>
								<span className="screen-reader-text">
									Location type:&nbsp;
								</span>
								{ decodeEntities( location_type ) }
							</span>
						) }
						{ !! url && (
							<p className="str-maps__dialog-cta-wrapper">
								<a href={ url } className="str-maps__dialog-cta btn" target={ isExternal ? '_blank' : '_self' } rel={ isExternal ? 'noopener noreferrer' : ''}>
								{ mapType === 'pif-map' ? 'View Partner' : 'View Network Member' }
								{
									isExternal && (
										<span>
											<ExternalLinkIcon />
										</span>
									)
								}
								</a>
							</p>
						) }
					</div>
				</>
			) }
		</dialog>
	);
}
